import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  MapPin, 
  Plus, 
  Search, 
  Car, 
  User, 
  Clock, 
  Loader2,
  ChevronRight,
  Phone,
  ExternalLink,
  CheckCircle,
  XCircle,
  AlertCircle,
  Truck,
  KeyRound,
  Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { SettlementApprovalChip } from '@/components/admin/SettlementApprovalChip';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Inline display of a pickup or delivery WhatsApp OTP, with copy-to-clipboard.
 * Renders a clear "code not generated yet" placeholder when the order doesn't
 * have an OTP yet (e.g. driver hasn't been assigned / hasn't started pickup).
 */
function UnlockCodeBlock({ kind, code, verified, t }) {
  const isPickup = kind === 'pickup';
  const labelKey = isPickup ? 'transport_tab_unlock_pickup_code' : 'transport_tab_unlock_delivery_code';
  const ringClass = isPickup
    ? 'border-emerald-300 bg-emerald-50'
    : 'border-rose-300 bg-rose-50';
  const codeColor = isPickup ? 'text-emerald-900' : 'text-rose-900';
  const labelColor = isPickup ? 'text-emerald-700' : 'text-rose-700';

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(String(code));
      toast.success(t('transport_tab_unlock_code_copied', { defaultValue: 'Código copiado' }));
    } catch {
      toast.error(t('transport_tab_unlock_code_copy_failed', { defaultValue: 'No se pudo copiar el código' }));
    }
  };

  return (
    <div className={`rounded-lg border ${ringClass} px-3 py-2.5`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`text-xs font-semibold uppercase tracking-wide ${labelColor}`}>
          {t(labelKey)}
        </span>
        {code && verified ? (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
            <CheckCircle className="w-3 h-3" />
            {t('transport_tab_unlock_code_verified', { defaultValue: 'Verificado' })}
          </span>
        ) : null}
      </div>
      {code ? (
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className={`text-xl font-mono font-bold tracking-[0.35em] ${codeColor} select-all`}>
            {code}
          </span>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
            aria-label={t('transport_tab_unlock_code_copy', { defaultValue: 'Copiar' })}
          >
            <Copy className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <p className="mt-1 text-sm text-gray-500 italic">
          {t('transport_tab_unlock_code_pending', {
            defaultValue: 'Pendiente — se generará al asignar conductor / iniciar peritaje.',
          })}
        </p>
      )}
    </div>
  );
}

const TransportServicesTab = () => {
  const { t, i18n } = useTranslation();
  const dateLocale = (i18n.language || '').startsWith('en') ? 'en-US' : 'es-ES';
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [creating, setCreating] = useState(false);
  const [etcSyncing, setEtcSyncing] = useState(false);
  
  const [newService, setNewService] = useState({
    client_id: '',
    vehicle_plate: '',
    vehicle_brand: '',
    vehicle_model: '',
    vehicle_color: '',
    pickup_address: '',
    delivery_address: '',
    scheduled_datetime: '',
    contact_phone: '',
    notes: ''
  });

  // Always load full list so stat cards and totals match the same dataset as filters (no ?status= shrink).
  const fetchServices = useCallback(async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = `${BACKEND_URL}/api/admin/transport-services`;
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServices(data.services || []);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchClients = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/users?role=client`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data.filter(u => u.role === 'client'));
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchDrivers = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/drivers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDrivers(data.drivers || data || []);
      }
    } catch (error) {
      console.error('Error fetching drivers:', error);
    }
  };

  const ensureClientsLoaded = useCallback(async () => {
    if (clients.length > 0) return;
    await fetchClients();
  }, [clients.length]);

  const ensureDriversLoaded = useCallback(async () => {
    if (drivers.length > 0) return;
    await fetchDrivers();
  }, [drivers.length]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleEtcSync = async () => {
    setEtcSyncing(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/integrations/eurotransportcar/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        toast.success(
          t('transport_tab_etc_sync_success', {
            defaultValue: 'Eurotransportcar: {{created}} nuevos, {{updated}} actualizados ({{fetched}} en ETC)',
            created: data.created ?? 0,
            updated: data.updated ?? 0,
            fetched: data.fetched ?? 0,
          })
        );
        await fetchServices();
      } else {
        toast.error(data.detail || t('transport_tab_etc_sync_failed', { defaultValue: 'Error al sincronizar con Eurotransportcar' }));
      }
    } catch (error) {
      console.error('ETC sync error:', error);
      toast.error(t('transport_tab_etc_sync_failed', { defaultValue: 'Error al sincronizar con Eurotransportcar' }));
    } finally {
      setEtcSyncing(false);
    }
  };

  const handleCreateService = async () => {
    if (!newService.client_id || !newService.vehicle_plate || !newService.pickup_address || !newService.delivery_address) {
      alert(t('transport_tab_alert_required_fields'));
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/transport-services`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({
          client_id: newService.client_id,
          vehicle_info: {
            plate: newService.vehicle_plate,
            brand: newService.vehicle_brand,
            model: newService.vehicle_model,
            color: newService.vehicle_color
          },
          pickup_location: {
            address: newService.pickup_address
          },
          delivery_location: {
            address: newService.delivery_address
          },
          scheduled_datetime: newService.scheduled_datetime,
          contact_phone: newService.contact_phone,
          notes: newService.notes
        })
      });
      
      if (response.ok) {
        setCreateModalOpen(false);
        setNewService({
          client_id: '',
          vehicle_plate: '',
          vehicle_brand: '',
          vehicle_model: '',
          vehicle_color: '',
          pickup_address: '',
          delivery_address: '',
          scheduled_datetime: '',
          contact_phone: '',
          notes: ''
        });
        fetchServices();
      } else {
        const error = await response.json();
        alert(error.detail || t('transport_tab_alert_create_failed'));
      }
    } catch (error) {
      console.error('Error creating service:', error);
      alert(t('transport_tab_alert_create_failed'));
    } finally {
      setCreating(false);
    }
  };

  const handleAssignDriver = async (serviceId, driverId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/transport-services/${serviceId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify({ driver_id: driverId })
      });
      
      if (response.ok) {
        fetchServices();
        if (selectedService?.id === serviceId) {
          const updated = await response.json();
          setSelectedService(updated);
        }
      }
    } catch (error) {
      console.error('Error assigning driver:', error);
    }
  };

  const fetchServiceDetail = async (serviceId) => {
    try {
      await ensureDriversLoaded();
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${BACKEND_URL}/api/admin/transport-services/${serviceId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedService(data);
        setDetailModalOpen(true);
      }
    } catch (error) {
      console.error('Error fetching service detail:', error);
    }
  };

  const openCreateServiceModal = async () => {
    await Promise.all([ensureClientsLoaded(), ensureDriversLoaded()]);
    setCreateModalOpen(true);
  };

  const displayedServices = useMemo(() => {
    const q = (searchTerm || '').trim().toLowerCase();
    return services.filter((service) => {
      if (statusFilter !== 'all' && service.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (service.vehicle_info?.plate || '').toLowerCase().includes(q) ||
        (service.driver_info?.name || '').toLowerCase().includes(q) ||
        (service.client_info?.name || '').toLowerCase().includes(q) ||
        (service.client_info?.company_name || '').toLowerCase().includes(q) ||
        (service.client_name || '').toLowerCase().includes(q) ||
        (service.client_email || '').toLowerCase().includes(q) ||
        (service.pickup_location?.address || '').toLowerCase().includes(q) ||
        (service.delivery_location?.address || '').toLowerCase().includes(q)
      );
    });
  }, [services, statusFilter, searchTerm]);

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: t('transport_tab_status_pending'), className: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
      reserved: { label: t('transport_tab_status_reserved'), className: 'bg-blue-100 text-blue-700', icon: User },
      in_progress: { label: t('transport_tab_status_in_route'), className: 'bg-green-100 text-green-700', icon: Truck },
      completed: { label: t('transport_tab_status_completed'), className: 'bg-gray-100 text-gray-700', icon: CheckCircle },
      cancelled: { label: t('transport_tab_status_cancelled'), className: 'bg-red-100 text-red-700', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.className} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const formatDate = (isoString) => {
    if (!isoString) return t('gps_live_na');
    const date = new Date(isoString);
    return date.toLocaleDateString(dateLocale, { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Bucket counts from full dataset (same rules as list filters when no status chip selected)
  const { pendingServices, reservedServices, inProgressServices, completedServices } = useMemo(() => ({
    pendingServices: services.filter((s) => s.status === 'pending'),
    reservedServices: services.filter((s) => s.status === 'reserved'),
    inProgressServices: services.filter((s) => s.status === 'in_progress'),
    completedServices: services.filter((s) => s.status === 'completed'),
  }), [services]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">{t('transport_tab_loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="transport-services-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="w-6 h-6 text-indigo-600" />
            {t('transport_tab_title')}
          </h2>
          <p className="text-gray-600 mt-1">
            {t('transport_tab_subtitle')}
          </p>
        </div>
        
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={handleEtcSync}
            disabled={etcSyncing}
          >
            {etcSyncing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <ExternalLink className="w-4 h-4 mr-2" />
            )}
            {t('transport_tab_etc_sync', { defaultValue: 'Sync Eurotransportcar' })}
          </Button>
          <Button onClick={openCreateServiceModal}>
            <Plus className="w-4 h-4 mr-2" />
            {t('transport_tab_new_service')}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('pending')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-yellow-600">{pendingServices.length}</p>
                <p className="text-sm text-gray-500">{t('transport_tab_stat_pending')}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-yellow-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('reserved')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-blue-600">{reservedServices.length}</p>
                <p className="text-sm text-gray-500">{t('transport_tab_stat_reserved')}</p>
              </div>
              <User className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('in_progress')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-green-600">{inProgressServices.length}</p>
                <p className="text-sm text-gray-500">{t('transport_tab_stat_in_route')}</p>
              </div>
              <Truck className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('completed')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-gray-600">{completedServices.length}</p>
                <p className="text-sm text-gray-500">{t('transport_tab_filter_completed')}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-gray-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setStatusFilter('all')}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold text-indigo-600">{services.length}</p>
                <p className="text-sm text-gray-500">{t('transport_tab_stat_total')}</p>
              </div>
              <MapPin className="w-8 h-8 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={t('transport_tab_search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder={t('transport_tab_filter_status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin_all')}</SelectItem>
            <SelectItem value="pending">{t('transport_tab_filter_pending')}</SelectItem>
            <SelectItem value="reserved">{t('transport_tab_filter_reserved')}</SelectItem>
            <SelectItem value="in_progress">{t('transport_tab_filter_in_route')}</SelectItem>
            <SelectItem value="completed">{t('transport_tab_filter_completed')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Services List */}
      {displayedServices.length > 0 ? (
        <div className="grid gap-4">
          {displayedServices.map((service) => (
            <Card 
              key={service.id} 
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => fetchServiceDetail(service.id)}
            >
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  {/* Vehicle Info */}
                  <div className="flex items-center gap-4">
                    <div className="p-3 bg-indigo-100 rounded-lg">
                      <Car className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-bold text-lg">{service.vehicle_info?.plate}</span>
                        {getStatusBadge(service.status)}
                        {service.external_source === 'eurotransportcar' && (
                          <Badge variant="outline" className="border-violet-300 text-violet-700 bg-violet-50">
                            ETC{service.external_id ? ` #${service.external_id}` : ''}
                          </Badge>
                        )}
                        <SettlementApprovalChip order={service} t={t} />
                      </div>
                      <p className="text-gray-600 text-sm">
                        {service.vehicle_info?.brand} {service.vehicle_info?.model}
                      </p>
                      {(service.client_info || service.client_name) && (
                        <p className="text-sm text-gray-500">
                          {t('admin_client_label')}{' '}
                          {service.client_info?.company_name ||
                            service.client_info?.name ||
                            service.client_name ||
                            '—'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Route */}
                  <div className="flex-1 max-w-md">
                    <div className="flex items-center gap-2 text-sm">
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="text-gray-600 truncate">{service.pickup_location?.address}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm mt-1">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <span className="text-gray-600 truncate">{service.delivery_location?.address}</span>
                    </div>
                  </div>

                  {/* Driver & Date */}
                  <div className="text-sm text-right">
                    {service.driver_info ? (
                      <div className="flex items-center gap-2 justify-end">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium">{service.driver_info.name}</span>
                      </div>
                    ) : (
                      <span className="text-yellow-600 font-medium">{t('no_driver')}</span>
                    )}
                    <div className="flex items-center gap-1 justify-end mt-1 text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(service.scheduled_datetime)}
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <MapPin className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900">{t('transport_tab_empty_title')}</h3>
              <p className="text-gray-500 mt-1">
                {searchTerm ? t('transport_tab_empty_search') : t('transport_tab_empty_hint')}
              </p>
              <Button className="mt-4" onClick={openCreateServiceModal}>
                <Plus className="w-4 h-4 mr-2" />
                {t('transport_tab_new_service')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Service Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('transport_tab_modal_create_title')}</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            {/* Client */}
            <div>
              <Label>{t('transport_tab_client_required')}</Label>
              <Select 
                value={newService.client_id} 
                onValueChange={(v) => setNewService({...newService, client_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('ph_select_client')} />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.company_name || client.name} ({client.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Vehicle Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('transport_tab_plate_required')}</Label>
                <Input 
                  value={newService.vehicle_plate}
                  onChange={(e) => setNewService({...newService, vehicle_plate: e.target.value.toUpperCase()})}
                  placeholder={t('transport_tab_ph_plate')}
                />
              </div>
              <div>
                <Label>{t('transport_tab_brand')}</Label>
                <Input 
                  value={newService.vehicle_brand}
                  onChange={(e) => setNewService({...newService, vehicle_brand: e.target.value})}
                  placeholder={t('transport_tab_ph_brand')}
                />
              </div>
              <div>
                <Label>{t('transport_tab_model')}</Label>
                <Input 
                  value={newService.vehicle_model}
                  onChange={(e) => setNewService({...newService, vehicle_model: e.target.value})}
                  placeholder={t('transport_tab_ph_model')}
                />
              </div>
              <div>
                <Label>{t('transport_tab_color')}</Label>
                <Input 
                  value={newService.vehicle_color}
                  onChange={(e) => setNewService({...newService, vehicle_color: e.target.value})}
                  placeholder={t('transport_tab_ph_color')}
                />
              </div>
            </div>

            {/* Locations */}
            <div>
              <Label>{t('transport_tab_pickup_address_required')}</Label>
              <Input 
                value={newService.pickup_address}
                onChange={(e) => setNewService({...newService, pickup_address: e.target.value})}
                placeholder={t('transport_tab_ph_pickup')}
              />
            </div>
            <div>
              <Label>{t('transport_tab_delivery_address_required')}</Label>
              <Input 
                value={newService.delivery_address}
                onChange={(e) => setNewService({...newService, delivery_address: e.target.value})}
                placeholder={t('transport_tab_ph_delivery')}
              />
            </div>

            {/* Schedule & Contact */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('transport_tab_scheduled_date')}</Label>
                <Input 
                  type="datetime-local"
                  value={newService.scheduled_datetime}
                  onChange={(e) => setNewService({...newService, scheduled_datetime: e.target.value})}
                />
              </div>
              <div>
                <Label>{t('transport_tab_contact_phone')}</Label>
                <Input 
                  value={newService.contact_phone}
                  onChange={(e) => setNewService({...newService, contact_phone: e.target.value})}
                  placeholder={t('transport_tab_ph_phone')}
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label>{t('transport_tab_notes')}</Label>
              <Textarea 
                value={newService.notes}
                onChange={(e) => setNewService({...newService, notes: e.target.value})}
                placeholder={t('transport_tab_notes_placeholder')}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              {t('admin_cancel')}
            </Button>
            <Button onClick={handleCreateService} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('transport_tab_create_service')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Service Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-2xl">
          {selectedService && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Car className="w-5 h-5" />
                  {selectedService.vehicle_info?.plate}
                  {getStatusBadge(selectedService.status)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Vehicle Details */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2">{t('transport_tab_detail_vehicle')}</h4>
                    <p className="text-gray-600">
                      {selectedService.vehicle_info?.brand} {selectedService.vehicle_info?.model}
                      {selectedService.vehicle_info?.color && ` - ${selectedService.vehicle_info.color}`}
                    </p>
                  </CardContent>
                </Card>

                {/* Route */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2">{t('transport_tab_detail_route')}</h4>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500 mt-1"></div>
                        <div>
                          <span className="text-sm text-gray-500">{t('pickup')}</span>
                          <p>{selectedService.pickup_location?.address}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500 mt-1"></div>
                        <div>
                          <span className="text-sm text-gray-500">{t('delivery')}</span>
                          <p>{selectedService.delivery_location?.address}</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Schedule & Contacts */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2">Schedule & Contact</h4>
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">{t('transport_tab_scheduled_date')}:</span>
                        <p>{formatDate(selectedService.scheduled_datetime)}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('transport_tab_contact_phone')}:</span>
                        <p>{selectedService.contact_phone || '—'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('pickup')}:</span>
                        <p>
                          {selectedService.pickup_date_from ? formatDate(selectedService.pickup_date_from) : '—'}
                          {selectedService.pickup_time_start ? ` · ${selectedService.pickup_time_start}` : ''}
                          {selectedService.pickup_time_end ? `-${selectedService.pickup_time_end}` : ''}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('delivery')}:</span>
                        <p>
                          {selectedService.delivery_date_from ? formatDate(selectedService.delivery_date_from) : '—'}
                          {selectedService.delivery_time_start ? ` · ${selectedService.delivery_time_start}` : ''}
                          {selectedService.delivery_time_end ? `-${selectedService.delivery_time_end}` : ''}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-500">Pickup contact:</span>
                        <p>{selectedService.pickup_contact_name || '—'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Delivery contact:</span>
                        <p>{selectedService.delivery_contact_name || '—'}</p>
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-2 mt-3">
                      <Badge variant="outline">{t('admin_status_label')}: {selectedService.status || '—'}</Badge>
                      <Badge variant="outline">{t('service_type')}: {selectedService.service_type || '—'}</Badge>
                      <Badge variant="outline">Source: {selectedService.source || '—'}</Badge>
                    </div>
                  </CardContent>
                </Card>

                {/* Driver Assignment */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2">{t('transport_tab_detail_driver')}</h4>
                    {selectedService.driver_info ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{selectedService.driver_info.name}</p>
                          {selectedService.driver_info.phone && (
                            <a href={`tel:${selectedService.driver_info.phone}`} className="text-blue-600 text-sm flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {selectedService.driver_info.phone}
                            </a>
                          )}
                        </div>
                        {selectedService.status === 'reserved' && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleAssignDriver(selectedService.id, null)}
                          >
                            {t('transport_tab_unassign_driver')}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div>
                        <p className="text-gray-500 mb-2">{t('transport_tab_no_driver_assigned_detail')}</p>
                        <Select onValueChange={(driverId) => handleAssignDriver(selectedService.id, driverId)}>
                          <SelectTrigger>
                            <SelectValue placeholder={t('transport_tab_assign_driver_placeholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {drivers.map(driver => (
                              <SelectItem key={driver.id} value={driver.id}>
                                {driver.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Client */}
                {(selectedService.client_info || selectedService.client_name) && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2">{t('transport_tab_detail_client')}</h4>
                      <p>
                        {selectedService.client_info?.company_name ||
                          selectedService.client_info?.name ||
                          selectedService.client_name ||
                          '—'}
                      </p>
                      {selectedService.client_info?.phone && (
                        <p className="text-sm text-gray-500">{selectedService.client_info.phone}</p>
                      )}
                      {selectedService.client_email && (
                        <p className="text-sm text-gray-500">{selectedService.client_email}</p>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Tracking Link */}
                {selectedService.tracking_url && (
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                    <span className="text-sm text-blue-700">{t('transport_tab_tracking_link')}</span>
                    <a 
                      href={selectedService.tracking_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-blue-600 hover:underline text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      {t('transport_tab_open')}
                    </a>
                  </div>
                )}

                {/* Unlock codes (pickup / delivery OTPs) — admin reads these out to clients/drivers */}
                <Card>
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <KeyRound className="w-4 h-4 text-[#0a3042]" />
                      {t('transport_tab_unlock_codes_title')}
                    </h4>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <UnlockCodeBlock
                        kind="pickup"
                        code={selectedService.pickup_confirmation_code}
                        verified={selectedService.pickup_code_verified}
                        t={t}
                      />
                      <UnlockCodeBlock
                        kind="delivery"
                        code={selectedService.delivery_confirmation_code}
                        verified={selectedService.delivery_code_verified}
                        t={t}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-3">
                      {t('transport_tab_unlock_codes_hint')}
                    </p>
                  </CardContent>
                </Card>

                {/* Inspections */}
                {(selectedService.pickup_inspection || selectedService.delivery_inspection) && (
                  <Card>
                    <CardContent className="p-4">
                      <h4 className="font-semibold mb-2">{t('transport_tab_inspections_section')}</h4>
                      <div className="space-y-2">
                        {selectedService.pickup_inspection && (
                          <Badge className="bg-blue-100 text-blue-700">{t('transport_tab_pickup_inspection_badge')}</Badge>
                        )}
                        {selectedService.delivery_inspection && (
                          <Badge className="bg-green-100 text-green-700">{t('transport_tab_delivery_inspection_badge')}</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TransportServicesTab;
